import type { ApplicationContext } from '../../styleApplicator.types';
import type { CSSObject, Sprite } from '../../themeOutput.types';

/**
 * The viewport-fixed style that needs no measurement: 'parent-fill' on the back layer resolves
 * to a constant "fill the window" box. Split out of computeFixedElementStyle so it can run
 * during render — server included — instead of waiting for a layout effect to apply constants
 * (PRNT-4898). Returns undefined for sprites whose geometry genuinely depends on the viewport.
 */
export function computeStaticFixedStyle(context: ApplicationContext, spriteConfig: Sprite, frameInset?: string): CSSObject | undefined {
  if (!spriteConfig.fixedPositionWhenScrolling) {
    return undefined;
  }
  if (spriteConfig.scale.constraint === 'parent-fill' && 'layer' in context && context.layer === 'back') {
    // This configuration effectively achieves 'parent fill to window'
    // Currently only targets Brannan outer frame
    return {
      position: 'fixed',
      top: '0px',
      left: '0px',
      height: '100vh',
      width: '100vw',
      transform: 'translate(0)',
    };
  }
  // 'original' + a caller-supplied frameInset: the only thing computeFixedElementStyle needs the
  // DOM for is the frame's horizontal box, and a layout that knows its own frame can state that
  // as a CSS length (Brannan's back frame is full-bleed => '0px'; its centered Page =>
  // 'max(0px, calc(50% - <halfMaxWidth>px))'). With it, every anchor below resolves in CSS
  // exactly as the measured translate did — the sprite box is explicitly sized to asset x factor,
  // so anchoring an edge equals offsetting by the computed distance. Running during render is the
  // point: the sprite ships visible in the SSR markup instead of hidden until a layout effect
  // measures. Vertical anchors never needed the frame (the sprite is viewport-fixed), and
  // 'center' never needs the inset either, since a centered frame shares the viewport's centre.
  if (spriteConfig.scale.constraint === 'original' && frameInset) {
    const factor = spriteConfig.scale.factor;
    const spriteWidth = spriteConfig.asset.width * factor;
    const spriteHeight = spriteConfig.asset.height * factor;
    const { x: offsetX, y: offsetY } = spriteConfig.offset;

    // The opposite anchor is reset to 'auto' because the sprite's own computedStyleObject sets
    // left and top to 0, and this style is merged after it.
    const horizontal: CSSObject =
      spriteConfig.placement.x === 'right'
        ? { left: 'auto', right: `calc(${frameInset} + ${offsetX}px)` }
        : spriteConfig.placement.x === 'center'
          ? { left: `calc(50% - ${spriteWidth / 2}px + ${offsetX}px)`, right: 'auto' }
          : { left: `calc(${frameInset} + ${offsetX}px)`, right: 'auto' };

    const vertical: CSSObject =
      spriteConfig.placement.y === 'bottom'
        ? { top: 'auto', bottom: `${offsetY}px` }
        : spriteConfig.placement.y === 'center'
          ? { top: `calc(50% - ${spriteHeight / 2}px + ${offsetY}px)`, bottom: 'auto' }
          : { top: `${offsetY}px`, bottom: 'auto' };

    return { position: 'fixed', ...horizontal, ...vertical, transform: 'translate3d(0, 0, 0)' };
  }
  return undefined;
}

/**
 * `view` is the window the sprite actually renders in — passed in rather than read from the
 * global so this stays SSR-safe and correct inside the website-designer preview iframe.
 */
export function computeFixedElementStyle(parentElement: Element, context: ApplicationContext, spriteConfig: Sprite, view: Window): CSSObject {
  if (spriteConfig.fixedPositionWhenScrolling) {
    if (spriteConfig.scale.constraint === 'original') {
      const factor = spriteConfig.scale.factor;
      // Confirm scroll

      const parentRect = parentElement.getBoundingClientRect();

      const windowHeight = view.innerHeight;

      const [spriteWidth, spriteHeight] = [spriteConfig.asset.width, spriteConfig.asset.height].map(value => value * factor);

      let distanceFromLeft = 0;
      let leftOfffsetFactor = 1;
      switch (spriteConfig.placement.x) {
        case 'left':
          distanceFromLeft = parentRect.left;
          break;
        // distanceFromLeft = <furthest left value> + <center of container>  | <center offset of sprite is handled in css>
        case 'center':
          distanceFromLeft = parentRect.left + parentRect.width / 2 - spriteWidth / 2;
          break;
        case 'right':
          leftOfffsetFactor = -1;
          // To achieve an effect of position: absolute right: 0, subtract by sprite width
          distanceFromLeft = parentRect.right - spriteWidth;
          break;
      }
      distanceFromLeft += spriteConfig.offset.x * leftOfffsetFactor;

      let verticalOrigin = 0;
      let verticalOffsetFactor = 1;
      switch (spriteConfig.placement.y) {
        case 'center':
          // 1. Move sprite down to center point of visible screen height
          // 2. Move sprite up by half of sprite height to achieve vertical centering
          verticalOrigin = windowHeight / 2 - spriteHeight / 2;
          break;
        case 'bottom':
          // 1. Move sprite down beyond the bottom of the visible screen
          // 2. Move sprite up by full sprite height to calculate a top offset
          verticalOrigin = windowHeight - spriteHeight;
          verticalOffsetFactor = -1;
          break;
      }
      verticalOrigin += spriteConfig.offset.y * verticalOffsetFactor;

      const computedTransform = `translate3d(${distanceFromLeft}px, ${verticalOrigin}px, 0px)`;
      return {
        position: 'fixed',
        left: '0px',
        top: '0px',
        willChange: 'transform',
        transform: computedTransform,
        WebkitTransform: computedTransform,
      };
    }
    const staticStyle = computeStaticFixedStyle(context, spriteConfig);
    if (staticStyle) {
      return staticStyle;
    }
  }
  return {};
}
